import { useState, useEffect, useRef } from "react"
import { useQueryState } from "nuqs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faSearch,
  faTimes,
  faDatabase,
  faTriangleExclamation,
  faChevronDown,
  faUsers,
} from "@fortawesome/free-solid-svg-icons"
import { DateTime } from "luxon"
import Dropdown from "../../components/Dropdown"
import ApiError from "../../components/ApiError"
import Loader from "../../components/Loader"
import {
  useVideoStatsCases,
  useVideoScanFilterOptions,
} from "../../hooks/useVideoLogs"

const ALL = "All"

const CARD_ACCENTS = ["violet", "amber", "teal", "sky", "rose", "gray"]

const formatLabel = (value) => (value || "").replace(/_/g, " ")

const TODAY = DateTime.now().toISODate()
const DEFAULT_FROM = DateTime.now().minus({ days: 6 }).toISODate()

const dateInputClass =
  "rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"

const formatCreatedDate = (value) => {
  if (!value) return "—"
  const dt =
    value instanceof Date
      ? DateTime.fromJSDate(value)
      : DateTime.fromISO(String(value))
  return dt.isValid ? dt.toFormat("MMM d, yyyy HH:mm") : "—"
}

const tileAccents = {
  blue: "bg-blue-50 text-blue-600",
  red: "bg-red-50 text-red-600",
  violet: "bg-violet-50 text-violet-600",
  amber: "bg-amber-50 text-amber-600",
  teal: "bg-teal-50 text-teal-600",
  sky: "bg-sky-50 text-sky-600",
  rose: "bg-rose-50 text-rose-600",
  gray: "bg-gray-100 text-gray-600",
}

const StatTile = ({
  label,
  value,
  valueSuffix,
  hint,
  icon,
  accent = "blue",
  sub,
  onClick,
  active,
}) => {
  const inner = (
    <>
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tileAccents[accent]}`}
      >
        <FontAwesomeIcon className="w-4 h-4" icon={icon} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-bold text-gray-900 leading-none tabular-nums">
          {(value ?? 0).toLocaleString()}
          {valueSuffix && (
            <span className="ml-2 text-sm font-bold text-gray-500">
              {valueSuffix}
            </span>
          )}
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mt-1">
          {label}
        </div>
        {sub && (
          <div className="text-sm font-bold text-gray-600 mt-0.5">{sub}</div>
        )}
      </div>
    </>
  )
  const base =
    "flex items-center gap-3 bg-white rounded-xl px-4 py-3 flex-1 min-w-[190px] shadow-sm text-left"

  if (onClick) {
    return (
      <button
        className={`${base} border transition-colors ${active ? "border-blue-500 ring-1 ring-blue-500" : "border-gray-200 hover:border-gray-300"}`}
        title={hint}
        type="button"
        onClick={onClick}
      >
        {inner}
      </button>
    )
  }
  return (
    <div className={`${base} border border-gray-200`} title={hint}>
      {inner}
    </div>
  )
}

const FieldChip = ({ value }) =>
  value ? (
    <span className="inline-block px-2 py-0.5 text-[10px] font-medium rounded-full bg-sky-50 border border-sky-200 text-sky-700 whitespace-nowrap">
      {value}
    </span>
  ) : (
    <span className="text-gray-400">—</span>
  )

const parseTrace = (raw) => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const humanizeStep = (value) =>
  (value || "")
    .split("_")
    .map((word) => (word ? word[0] + word.slice(1).toLowerCase() : word))
    .join(" ")

const ResolutionTrace = ({ trace }) => {
  const steps = parseTrace(trace)

  if (!steps.length)
    return <p className="text-sm text-gray-400">No trace recorded.</p>

  return (
    <ol className="flex flex-col">
      {steps.map((step, stepIndex) => {
        const isLast = stepIndex === steps.length - 1
        const dotClass =
          step.result === "FOUND"
            ? "bg-green-500"
            : step.result === "NOT_FOUND"
              ? "bg-red-500"
              : "bg-blue-500"
        return (
          <li key={stepIndex} className="relative pl-8 pb-4 last:pb-0">
            {!isLast && (
              <span className="absolute left-[11px] top-7 bottom-0 w-0.5 bg-gray-100" />
            )}
            <span
              className={`absolute left-0 top-1 flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold text-white ring-4 ring-white ${dotClass}`}
            >
              {stepIndex + 1}
            </span>
            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-semibold text-gray-800">
                  {humanizeStep(step.step)}
                </span>
                {step.result ? (
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                      step.result === "FOUND"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {step.result}
                  </span>
                ) : null}
              </div>
              {step.query ? (
                <div className="mt-1 text-xs text-gray-500 break-words">
                  <span className="font-medium text-gray-600">query:</span>{" "}
                  {step.query}
                </div>
              ) : null}
              {Array.isArray(step.npis) && step.npis.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {step.npis.map((npi) => (
                    <span
                      key={npi}
                      className="font-mono text-[11px] rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-gray-600"
                    >
                      {npi}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

const TraceDrawer = ({ record, onClose }) => {
  if (!record) return null
  const steps = parseTrace(record.resolutionTrace)
  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm"
        role="presentation"
        onClick={onClose}
      />
      <div className="fixed right-0 top-0 bottom-0 z-50 flex w-[460px] max-w-[92vw] flex-col overflow-hidden rounded-l-2xl bg-white shadow-2xl ring-1 ring-black/5">
        <div className="relative bg-gradient-to-br from-blue-600 to-indigo-600 px-5 py-5 text-white">
          <button
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors"
            title="Close"
            type="button"
            onClick={onClose}
          >
            <FontAwesomeIcon className="w-3.5 h-3.5" icon={faTimes} />
          </button>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-100">
            Accession
          </div>
          <div className="font-mono text-lg font-bold tracking-tight">
            {record.accessionNo}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {record.resolutionMethod ? (
              <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-semibold">
                {record.resolutionMethod}
              </span>
            ) : null}
            <span className="text-xs text-blue-100">
              {formatCreatedDate(record.createdDate)}
            </span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              Resolution trace
            </p>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
              {steps.length} step{steps.length === 1 ? "" : "s"}
            </span>
          </div>
          <ResolutionTrace trace={record.resolutionTrace} />
        </div>
      </div>
    </>
  )
}

const RefCard = ({ group, onOpenTrace }) => {
  const [expanded, setExpanded] = useState(false)

  const accessions = group.accessions || []
  const count = accessions.length
  const methods = [
    ...new Set(accessions.map((a) => a.resolutionMethod).filter(Boolean)),
  ]
  const name =
    [group.firstName, group.lastName].filter(Boolean).join(" ") ||
    group.refName ||
    "Unknown"

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
      <button
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate">
            {name}
          </div>
          <div className="text-xs text-gray-500">
            {formatCreatedDate(group.createdDate)}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {methods.map((method) => (
            <FieldChip key={method} value={method} />
          ))}
          <span className="text-xs font-medium text-gray-600 tabular-nums whitespace-nowrap">
            {count} record{count === 1 ? "" : "s"}
          </span>
          <FontAwesomeIcon
            className={`w-3 h-3 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`}
            icon={faChevronDown}
          />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 flex flex-col gap-2">
          {accessions.length ? (
            accessions.map((accession) => (
              <div
                key={accession.accessionNo}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-100 bg-gray-50/50 p-2.5"
              >
                <button
                  className="font-mono text-sm font-semibold text-blue-600 hover:underline"
                  type="button"
                  onClick={() => onOpenTrace(accession)}
                >
                  {accession.accessionNo}
                </button>
                <FieldChip value={accession.resolutionMethod} />
                <span className="text-[11px] text-gray-400">
                  {formatCreatedDate(accession.createdDate)}
                </span>
              </div>
            ))
          ) : (
            <span className="text-xs text-gray-400">No records</span>
          )}
        </div>
      )}
    </div>
  )
}

const VideoStats = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [traceRecord, setTraceRecord] = useState(null)
  const [activeSearchTerm, setActiveSearchTerm] = useQueryState("search", {
    defaultValue: "",
  })
  const [from, setFrom] = useQueryState("from", { defaultValue: DEFAULT_FROM })
  const [to, setTo] = useQueryState("to", { defaultValue: TODAY })
  const [issuer, setIssuer] = useQueryState("issuer", { defaultValue: ALL })
  const [category, setCategory] = useQueryState("category", {
    defaultValue: "NPPES_API",
  })

  const sentinelRef = useRef(null)

  const { data: filterOptions } = useVideoScanFilterOptions()

  const getDateFilterValue = () => {
    if (!from && !to) return undefined
    return [from || "", to || ""].join(",")
  }

  const filters = {
    searchTerm: activeSearchTerm?.trim() || undefined,
    timePeriod: getDateFilterValue(),
    patientIssuer: issuer === ALL ? undefined : issuer,
    category,
  }

  const {
    data: { summary, categories, category: activeCategory, groups, total } = {
      summary: { totalRecords: 0 },
      categories: [],
      category: null,
      groups: [],
      total: 0,
    },
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useVideoStatsCases({ limit: 20, filters })

  const selectedLabel = activeCategory || category || ""

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: "200px", threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    setSearchTerm(activeSearchTerm || "")
  }, [activeSearchTerm])

  const handleClearFilters = () => {
    setFrom(DEFAULT_FROM)
    setTo(TODAY)
    setIssuer(ALL)
  }

  const handleClearSearch = () => {
    setSearchTerm("")
    setActiveSearchTerm("")
  }

  const hasActiveFilters =
    from !== DEFAULT_FROM || to !== TODAY || issuer !== ALL

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="p-4">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">
          Video Source Insights
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Showing video stats by source
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label className="text-xs font-medium mb-1 ml-1 text-gray-700">
              DATE RANGE
            </label>
            <div className="flex items-center gap-2">
              <input
                className={dateInputClass}
                max={to || TODAY}
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
              <span className="text-gray-400">–</span>
              <input
                className={dateInputClass}
                max={TODAY}
                min={from || undefined}
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
          </div>

          <Dropdown
            label="ISSUER"
            options={[ALL, ...(filterOptions?.patientIssuers || [])]}
            setValue={setIssuer}
            title="Filter by issuer"
            value={issuer}
          />

          <Dropdown
            label="MATCH SOURCE"
            options={categories.map((c) => formatLabel(c.key))}
            setValue={(display) =>
              setCategory(
                categories.find((c) => formatLabel(c.key) === display)?.key ||
                  ""
              )
            }
            title="Filter the table by match source"
            value={formatLabel(selectedLabel)}
          />

          <button
            className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!hasActiveFilters}
            title="Reset filters to their defaults"
            onClick={handleClearFilters}
          >
            Reset
          </button>

          <div className="flex-1" />

          <div>
            <label className="block text-xs font-medium mb-1 ml-1 text-gray-700">
              ACCESSION / PHYSICIAN
            </label>
            <div className="relative">
              <FontAwesomeIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
                icon={faSearch}
              />
              <input
                className="w-full min-w-[300px] pl-9 pr-9 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search and press Enter"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    setActiveSearchTerm(searchTerm)
                  }
                }}
              />
              {searchTerm && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  title="Clear search"
                  type="button"
                  onClick={handleClearSearch}
                >
                  <FontAwesomeIcon className="w-3 h-3" icon={faTimes} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-3">
        <StatTile
          accent="blue"
          hint="Total records logged in this period"
          icon={faDatabase}
          label="Total Records"
          value={summary.totalRecords}
        />
        {categories.map((c, index) => (
          <StatTile
            key={c.key}
            accent={CARD_ACCENTS[index % CARD_ACCENTS.length]}
            hint={`Records with match source ${c.key}`}
            icon={faUsers}
            label={formatLabel(c.key)}
            sub={`${
              summary.totalRecords
                ? Math.round((c.count / summary.totalRecords) * 100)
                : 0
            }% of total`}
            value={c.count}
          />
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20 gap-4">
          <Loader />
          <span className="text-gray-600">Loading error stats...</span>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-2 px-1">
            <h2 className="text-sm font-semibold text-gray-700">
              {formatLabel(selectedLabel)}
              <span className="ml-2 font-normal text-xs text-gray-500">
                {total.toLocaleString()} referring physician
                {total === 1 ? "" : "s"}
              </span>
            </h2>
            {activeSearchTerm && (
              <span className="text-xs text-gray-500">
                Filtered by{" "}
                <span className="font-semibold text-gray-700">
                  {activeSearchTerm}
                </span>
              </span>
            )}
          </div>

          {groups.length > 0 ? (
            <div className="flex flex-col gap-2 max-h-[calc(100vh-430px)] overflow-auto pr-1">
              {groups.map((group, index) => (
                <RefCard
                  key={`${group.refName || "unknown"}-${index}`}
                  group={group}
                  onOpenTrace={setTraceRecord}
                />
              ))}

              <div ref={sentinelRef} className="h-1" />

              {hasNextPage && (
                <div className="flex justify-center py-4">
                  {isFetchingNextPage ? (
                    <div className="flex items-center gap-2">
                      <Loader />
                      <span className="text-gray-600">
                        Loading more records...
                      </span>
                    </div>
                  ) : (
                    <button
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors text-sm font-medium"
                      onClick={fetchNextPage}
                    >
                      Load More
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-16 bg-white border border-gray-200 rounded-xl">
              <FontAwesomeIcon
                className="w-8 h-8 text-gray-300"
                icon={faTriangleExclamation}
              />
              <p className="text-sm text-gray-500">
                No records for this category and these filters.
              </p>
            </div>
          )}
        </div>
      )}

      <TraceDrawer record={traceRecord} onClose={() => setTraceRecord(null)} />
    </div>
  )
}

export default VideoStats
